/**
 * Agreement Detail Modal
 *
 * Shows agreement details with update history and allows adding progress updates.
 * Used by both employees (action holders) and managers.
 */

import { useState, useRef } from 'react';
import { useAgreementWithUpdates, useAddAgreementUpdate, useDeleteAgreement, useUpdateAgreement, useUpdateAgreementUpdate } from './useAgreements';
import { agreementsApi } from '../../lib/agreementsApi';
import type { ScoreAgreementStatus, AgreementUpdateType } from '../../lib/evaluationsApi';
import { useAuth } from '../auth/AuthContext';

interface AgreementDetailModalProps {
  agreementId: number;
  onClose: () => void;
  canEdit: boolean;
}

const STATUS_LABELS: Record<ScoreAgreementStatus, { label: string; color: string; bgColor: string }> = {
  concept: { label: 'Concept', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  definitief: { label: 'Definitief', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  afgerond: { label: 'Afgerond', color: 'text-green-700', bgColor: 'bg-green-100' },
  geannuleerd: { label: 'Geannuleerd', color: 'text-red-700', bgColor: 'bg-red-100' },
};

const UPDATE_TYPE_LABELS: Record<AgreementUpdateType, { label: string; icon: string }> = {
  voortgang: { label: 'Voortgang', icon: '📝' },
  status_wijziging: { label: 'Status wijziging', icon: '🔄' },
  notitie: { label: 'Notitie', icon: '💬' },
};

const AUTHOR_ROLE_LABELS: Record<string, string> = {
  medewerker: 'Medewerker',
  beoordelaar: 'Leidinggevende',
  hr: 'HR',
  admin: 'Admin',
};

export function AgreementDetailModal({ agreementId, onClose, canEdit }: AgreementDetailModalProps) {
  const { data, isLoading, error } = useAgreementWithUpdates(agreementId);
  const addUpdate = useAddAgreementUpdate();
  const deleteAgreement = useDeleteAgreement();
  const updateAgreement = useUpdateAgreement();
  const updateAgreementUpdate = useUpdateAgreementUpdate();
  const { user } = useAuth();

  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateType, setUpdateType] = useState<AgreementUpdateType>('voortgang');
  const [updateContent, setUpdateContent] = useState('');
  const [newStatus, setNewStatus] = useState<ScoreAgreementStatus | ''>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Edit states
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [editingUpdateId, setEditingUpdateId] = useState<number | null>(null);
  const [editedUpdateContent, setEditedUpdateContent] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

  const agreement = data?.data;

  // Check if current user is the creator of the agreement
  // If createdBy is null (legacy data), allow users with edit rights to edit
  const isCreator = user?.id && (agreement?.createdBy === user.id || !agreement?.createdBy);

  // Check if agreement is in concept status
  const isConcept = agreement?.status === 'concept';

  // User can delete if: status is concept AND user is NOT medewerker (beoordelaar, hr, admin can delete)
  const canDelete = canEdit && isConcept && user?.role && ['beoordelaar', 'hr', 'admin'].includes(user.role);

  // User can edit description if: they are the creator (or legacy data without createdBy) AND status is concept AND they have edit rights
  const canEditDescription = canEdit && isCreator && isConcept;

  const handleDelete = async () => {
    try {
      await deleteAgreement.mutateAsync(agreementId);
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  // Handle editing agreement description
  const handleStartEditDescription = () => {
    if (agreement) {
      setEditedDescription(agreement.beschrijving);
      setIsEditingDescription(true);
    }
  };

  const handleSaveDescription = async () => {
    if (!editedDescription.trim()) return;
    try {
      await updateAgreement.mutateAsync({
        agreementId,
        beschrijving: editedDescription.trim(),
      });
      setIsEditingDescription(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleCancelEditDescription = () => {
    setIsEditingDescription(false);
    setEditedDescription('');
  };

  // Handle editing agreement update
  const handleStartEditUpdate = (updateId: number, content: string) => {
    setEditingUpdateId(updateId);
    setEditedUpdateContent(content);
  };

  const handleSaveUpdateEdit = async () => {
    if (!editedUpdateContent.trim() || !editingUpdateId) return;
    try {
      await updateAgreementUpdate.mutateAsync({
        updateId: editingUpdateId,
        content: editedUpdateContent.trim(),
        agreementId,
      });
      setEditingUpdateId(null);
      setEditedUpdateContent('');
    } catch {
      // Error handled by mutation
    }
  };

  const handleCancelEditUpdate = () => {
    setEditingUpdateId(null);
    setEditedUpdateContent('');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError('');

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError('Bestand is te groot (max 3MB)');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(',')[1] || '';
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleDownloadAttachment = async (updateId: number) => {
    try {
      setDownloadingId(updateId);
      const response = await agreementsApi.getUpdateAttachment(updateId);
      const { name, type, data: base64Data } = response.data;

      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSubmitUpdate = async () => {
    if (!updateContent.trim()) return;

    try {
      let attachmentData: {
        attachmentName?: string;
        attachmentType?: string;
        attachmentSize?: number;
        attachmentData?: string;
      } = {};

      if (selectedFile) {
        const base64 = await fileToBase64(selectedFile);
        attachmentData = {
          attachmentName: selectedFile.name,
          attachmentType: selectedFile.type,
          attachmentSize: selectedFile.size,
          attachmentData: base64,
        };
      }

      await addUpdate.mutateAsync({
        agreementId,
        input: {
          updateType,
          content: updateContent.trim(),
          ...(updateType === 'status_wijziging' && newStatus ? { newStatus } : {}),
          ...attachmentData,
        },
      });
      setUpdateContent('');
      setNewStatus('');
      setSelectedFile(null);
      setFileError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setShowUpdateForm(false);
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Laden...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !agreement) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          <div className="text-center py-8">
            <p className="text-red-600">Er is een fout opgetreden bij het laden van de afspraak.</p>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
              Sluiten
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_LABELS[agreement.status];
  const isOpen = agreement.status !== 'afgerond' && agreement.status !== 'geannuleerd';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {agreement.agreementTypeName && (
                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                  {agreement.agreementTypeIcon} {agreement.agreementTypeName}
                </span>
              )}
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
              {agreement.isEmployeeActionHolder && (
                <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                  Medewerker actiehouder
                </span>
              )}
            </div>
            <h2 className="text-lg font-bold text-gray-900">Afspraak Details</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Agreement Info */}
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-medium text-gray-500">Beschrijving</h3>
                {canEditDescription && !isEditingDescription && (
                  <button
                    onClick={handleStartEditDescription}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Bewerken
                  </button>
                )}
              </div>
              {isEditingDescription ? (
                <div className="space-y-2">
                  <textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={handleCancelEditDescription}
                      className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                      disabled={updateAgreement.isPending}
                    >
                      Annuleren
                    </button>
                    <button
                      onClick={handleSaveDescription}
                      disabled={!editedDescription.trim() || updateAgreement.isPending}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {updateAgreement.isPending ? 'Opslaan...' : 'Opslaan'}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-900">{agreement.beschrijving}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              {agreement.deadline && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Deadline</h3>
                  <p className="text-gray-900">{new Date(agreement.deadline).toLocaleDateString('nl-NL')}</p>
                </div>
              )}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Vastgelegd op</h3>
                <p className="text-gray-900">{new Date(agreement.datumVastgelegd).toLocaleDateString('nl-NL')}</p>
              </div>
            </div>

            {agreement.evaluationPeriod && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Beoordelingsperiode</h3>
                <p className="text-gray-900">
                  {new Date(agreement.evaluationPeriod.start).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })} - {new Date(agreement.evaluationPeriod.end).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
                </p>
              </div>
            )}
          </div>

          {/* Updates History */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Voortgang & Updates</h3>
              {canEdit && isOpen && !showUpdateForm && (
                <button
                  onClick={() => setShowUpdateForm(true)}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  + Update toevoegen
                </button>
              )}
            </div>

            {/* Add Update Form */}
            {showUpdateForm && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type update</label>
                    <select
                      value={updateType}
                      onChange={(e) => setUpdateType(e.target.value as AgreementUpdateType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="voortgang">Voortgang</option>
                      <option value="status_wijziging">Status wijziging</option>
                      <option value="notitie">Notitie</option>
                    </select>
                  </div>
                  {updateType === 'status_wijziging' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nieuwe status</label>
                      <select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value as ScoreAgreementStatus)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Selecteer status</option>
                        <option value="definitief">Definitief</option>
                        <option value="afgerond">Afgerond</option>
                        <option value="geannuleerd">Geannuleerd</option>
                      </select>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving</label>
                  <textarea
                    rows={3}
                    value={updateContent}
                    onChange={(e) => setUpdateContent(e.target.value)}
                    placeholder="Beschrijf de voortgang of update..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bijlage (optioneel, max 3MB)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {selectedFile && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          setFileError('');
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {selectedFile && (
                    <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                  {fileError && (
                    <p className="mt-1 text-xs text-red-600">{fileError}</p>
                  )}
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      setShowUpdateForm(false);
                      setUpdateContent('');
                      setNewStatus('');
                      setSelectedFile(null);
                      setFileError('');
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={handleSubmitUpdate}
                    disabled={!updateContent.trim() || (updateType === 'status_wijziging' && !newStatus) || addUpdate.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {addUpdate.isPending ? 'Opslaan...' : 'Opslaan'}
                  </button>
                </div>
              </div>
            )}

            {/* Updates List */}
            <div className="space-y-3">
              {agreement.updates && agreement.updates.length > 0 ? (
                agreement.updates.map((update) => {
                  const typeInfo = UPDATE_TYPE_LABELS[update.updateType];
                  const isAuthor = user?.id === update.authorId;
                  const isEditingThisUpdate = editingUpdateId === update.id;
                  return (
                    <div key={update.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{typeInfo.icon} {typeInfo.label}</span>
                          <span>•</span>
                          <span>{update.authorName || 'Onbekend'}</span>
                          <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">
                            {AUTHOR_ROLE_LABELS[update.authorRole] || update.authorRole}
                          </span>
                          <span>•</span>
                          <span>{new Date(update.createdAt).toLocaleString('nl-NL', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}</span>
                          {update.hasAttachment && (
                            <button
                              onClick={() => handleDownloadAttachment(update.id)}
                              disabled={downloadingId === update.id}
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                              title={`Download ${update.attachmentName || 'bijlage'}`}
                            >
                              {downloadingId === update.id ? (
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                        {/* Edit button for update author */}
                        {canEdit && isAuthor && !isEditingThisUpdate && (
                          <button
                            onClick={() => handleStartEditUpdate(update.id, update.content)}
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        )}
                      </div>
                      {isEditingThisUpdate ? (
                        <div className="space-y-2">
                          <textarea
                            value={editedUpdateContent}
                            onChange={(e) => setEditedUpdateContent(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={handleCancelEditUpdate}
                              className="px-2 py-1 text-xs text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                              disabled={updateAgreementUpdate.isPending}
                            >
                              Annuleren
                            </button>
                            <button
                              onClick={handleSaveUpdateEdit}
                              disabled={!editedUpdateContent.trim() || updateAgreementUpdate.isPending}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              {updateAgreementUpdate.isPending ? 'Opslaan...' : 'Opslaan'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-900">{update.content}</p>
                      )}
                      {update.updateType === 'status_wijziging' && update.previousStatus && update.newStatus && (
                        <p className="mt-1 text-xs text-gray-500">
                          Status: {STATUS_LABELS[update.previousStatus]?.label} → {STATUS_LABELS[update.newStatus]?.label}
                        </p>
                      )}
                      {update.hasAttachment && update.attachmentName && (
                        <button
                          onClick={() => handleDownloadAttachment(update.id)}
                          disabled={downloadingId === update.id}
                          className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          {update.attachmentName}
                          {update.attachmentSize && (
                            <span className="text-gray-400">
                              ({(update.attachmentSize / 1024).toFixed(1)} KB)
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-6 text-gray-500 text-sm">
                  <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Nog geen updates toegevoegd
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-lg">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Afspraak verwijderen?</h3>
                  <p className="text-sm text-gray-500">Deze actie kan niet ongedaan worden gemaakt.</p>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={deleteAgreement.isPending}
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteAgreement.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteAgreement.isPending ? 'Verwijderen...' : 'Verwijderen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between">
            {canDelete ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Verwijderen
              </button>
            ) : (
              <div />
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Sluiten
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
